// src/App.js
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";

/* ================= CONTEXT ================= */

import { AuthProvider } from "./context/AuthContext";
import RequireAuth from "./components/RequireAuth";
import RequireAdmin from "./components/RequireAdmin";
import ScrollToTop from "./components/ScrollToTop";
import SeoManager from "./components/SeoManager";
import CookieBanner from "./components/CookieBanner";
import AnalyticsTracker from "./components/AnalyticsTracker";
import VersionUpdateBanner from "./components/VersionUpdateBanner";

/* ================= LAYOUTS ================= */

import Layout from "./components/Layout";
import DashboardLayout from "./components/DashboardLayout";
import LegalLayout from "./components/LegalLayout";

/* ================= READER ================= */

import CourseReaderPage from "./components/reader/CourseReaderPage";

/* ================= PUBLIC ================= */

import Home from "./pages/Home";
import Login from "./pages/auth/Login";
import Logout from "./pages/auth/Logout";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import ActivateAccount from "./pages/auth/ActivateAccount";
import FounderEditionInterest from "./pages/FounderEditionInterest";
import StorePublicLanding from "./pages/StorePublicLanding";

import Metodologia from "./pages/Metodologia";

import ZedroNewsPage from "./pages/newsletter/ZedroNewsPage";
import NewsletterIssuePage from "./pages/newsletter/NewsletterIssuePage";

import EditorialPublicPage from "./pages/admin/produccion/editorial/EditorialPublicPage";
import PreferencesPage from "./pages/PreferencesPage";
import UnsubscribePage from "./pages/UnsubscribePage";

import PrintView from "./pages/print/PrintView";

/* ================= PRIVATE ================= */

import Welcome from "./pages/Welcome";
import Dashboard from "./pages/Dashboard";

import SystemActive from "./pages/SystemActive";
import SystemDay from "./pages/SystemDay";

import CoreGuard from "./components/CoreGuard";

import CoreSystems from "./pages/CoreSystems";
import Bitacora from "./pages/Bitacora";
import Conexion from "./pages/Conexion";

import ToolsPanel from "./pages/ToolsPanel";
import AccountBilling from "./pages/AccountBilling";

/* ================= LEGAL ================= */

import LegalNotice from "./pages/legal/LegalNotice";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import CookiesPolicy from "./pages/legal/CookiesPolicy";
import TermsConditions from "./pages/legal/TermsConditions";
import CancellationRefundPolicy from "./pages/legal/CancellationRefundPolicy";
import Disclaimer from "./pages/legal/Disclaimer";

/* ================= ADMIN ================= */

import AdminCentralHome from "./pages/admin/AdminCentralHome";
import AdminFounderEditionInterests from "./pages/admin/AdminFounderEditionInterests";

import ProduccionHome from "./pages/admin/ProduccionHome";

import AdminSystems from "./pages/admin/produccion/systems/AdminSystems";
import AdminSections from "./pages/admin/produccion/systems/AdminSections";
import AdminLessons from "./pages/admin/produccion/systems/AdminLessons";
import AdminItems from "./pages/admin/produccion/systems/AdminItems";

import ProduccionMediaHome from "./pages/admin/produccion/media/ProduccionMediaHome";

import EditorialHome from "./pages/admin/produccion/editorial/EditorialHome";
import EditorialBuilder from "./pages/admin/produccion/editorial/EditorialBuilder";
import EditorialEdit from "./pages/admin/produccion/editorial/EditorialEdit";

import ProductsList from "./pages/admin/ProductsList";
import ProductCreate from "./pages/admin/ProductCreate";
import ProductDetail from "./pages/admin/ProductDetail";

import AdminMembers from "./pages/admin/AdminMembers";
import AdminMemberDetailWithPurchases from "./pages/admin/AdminMemberDetailWithPurchases";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";

/* ================= UTIL ================= */

import UnderConstruction from "./pages/UnderConstruction";

/* ================= NUEVA ARQUITECTURA ================= */

import ModuleLanding from "./pages/ModuleLanding";
import SubmoduleFormats from "./pages/SubmoduleFormats";

/* ================= STYLES ================= */

import "./styles/GlobalStyles.css";
import "./styles/reader.css";
import "./styles/phase6-closure.css";

/* ================= COMPAT ================= */

function RedirectModuleFormatsToModuleLanding() {
  const { moduleId } = useParams();
  return <Navigate to={`/module/${moduleId}`} replace />;
}

function RedirectNewsletterToZedroNews({ language = "es" }) {
  const { slug } = useParams();
  const basePath = language === "fr" ? "/fr/zedro-news" : "/zedro-news";
  return <Navigate to={`${basePath}/${slug}`} replace />;
}

/* ================= APP ================= */

export default function App() {
  return (
    <BrowserRouter>
      <VersionUpdateBanner />
      <AnalyticsTracker />
      <ScrollToTop />
      <SeoManager />
      <CookieBanner />

      <AuthProvider>
        <Routes>
          {/* ================= PUBLIC ================= */}
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/activate/:token" element={<ActivateAccount />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/edicion-fundadora" element={<FounderEditionInterest />} />
            <Route path="/fr/edition-fondatrice" element={<FounderEditionInterest />} />
            <Route path="/metodologia" element={<Metodologia />} />
            <Route path="/zedro-news" element={<ZedroNewsPage language="es" />} />
            <Route path="/zedro-news/:slug" element={<NewsletterIssuePage language="es" />} />
            <Route path="/newsletter/:slug" element={<RedirectNewsletterToZedroNews language="es" />} />
            <Route path="/fr/zedro-news" element={<ZedroNewsPage language="fr" />} />
            <Route path="/fr/zedro-news/:slug" element={<NewsletterIssuePage language="fr" />} />
            <Route path="/fr/newsletter/:slug" element={<RedirectNewsletterToZedroNews language="fr" />} />
            <Route path="/editorial/:slug" element={<EditorialPublicPage />} />
            <Route path="/preferences" element={<PreferencesPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/print/editorial" element={<PrintView />} />
            <Route path="/store" element={<StorePublicLanding />} />

            <Route path="/legal" element={<LegalLayout />}>
              <Route index element={<Navigate to="/legal/aviso-legal" replace />} />
              <Route path="aviso-legal" element={<LegalNotice />} />
              <Route path="privacidad" element={<PrivacyPolicy />} />
              <Route path="cookies" element={<CookiesPolicy />} />
              <Route path="terminos" element={<TermsConditions />} />
              <Route path="cancelacion-reembolsos" element={<CancellationRefundPolicy />} />
              <Route path="descargo" element={<Disclaimer />} />
            </Route>
          </Route>

          {/* ================= PRIVATE ================= */}
          <Route element={<RequireAuth />}>
            <Route element={<DashboardLayout />}>
              <Route path="/welcome" element={<Welcome />} />

              {/* Fase 6: el dashboard pertenece a la cuenta autenticada, no a una membresía concreta. */}
              <Route path="/dashboard" element={<Dashboard />} />

              <Route path="/account/billing" element={<AccountBilling />} />
              <Route path="/cuenta" element={<Navigate to="/account/billing" replace />} />

              {/* Catálogo Core legacy: se conserva aislado durante la transición. */}
              <Route path="/core" element={<CoreGuard><CoreSystems /></CoreGuard>} />

              {/* Fase 6: el backend decide el acceso al recurso según el producto autorizado. */}
              <Route path="/core/system/:systemId" element={<SystemActive />} />
              <Route path="/core/day/:dayId" element={<SystemDay />} />

              <Route path="/tools" element={<ToolsPanel />} />

              {/* La Bitácora es historial de la cuenta: permanece accesible aun si una membresía termina. */}
              <Route path="/bitacora" element={<Bitacora />} />

              {/* Conexión conserva por ahora su control comercial legacy; no se convierte en beneficio global de cuenta. */}
              <Route path="/conexion" element={<CoreGuard><Conexion /></CoreGuard>} />
              <Route path="/dashboard/zedro-news" element={<ZedroNewsPage language="es" basePath="/dashboard/zedro-news" />} />
              <Route path="/dashboard/zedro-news/:slug" element={<NewsletterIssuePage language="es" />} />
              <Route path="/guia" element={<CoreGuard><UnderConstruction title="Guía ZEDRO" /></CoreGuard>} />
              <Route path="/cultura" element={<CoreGuard><UnderConstruction title="Cultura ZEDRO" /></CoreGuard>} />

              {/* ===== NUEVA ARQUITECTURA ===== */}
              <Route path="/module/:moduleId" element={<ModuleLanding />} />
              <Route path="/module/:moduleId/sub/:subId/formats" element={<SubmoduleFormats />} />
              <Route path="/module/:moduleId/formats" element={<RedirectModuleFormatsToModuleLanding />} />

              {/* ===== READER ===== */}
              <Route path="/systems/:systemId/sections/:sectionId/lessons/:lessonId" element={<CourseReaderPage />} />
            </Route>
          </Route>

          {/* ================= ADMIN ================= */}
          <Route element={<RequireAdmin />}>
            <Route path="/admin" element={<AdminCentralHome />} />
            <Route path="/admin/edicion-fundadora" element={<AdminFounderEditionInterests />} />
            <Route path="/admin/produccion" element={<ProduccionHome />} />
            <Route path="/admin/systems" element={<AdminSystems />} />
            <Route path="/admin/systems/:systemId/sections" element={<AdminSections />} />
            <Route path="/admin/systems/:systemId/sections/:sectionId/lessons" element={<AdminLessons />} />
            <Route path="/admin/systems/:systemId/sections/:sectionId/lessons/:lessonId/items" element={<AdminItems />} />
            <Route path="/admin/produccion/media" element={<ProduccionMediaHome />} />
            <Route path="/admin/produccion/editorial" element={<EditorialHome />} />
            <Route path="/admin/produccion/editorial/new" element={<EditorialEdit mode="new" />} />
            <Route path="/admin/produccion/editorial/:id/edit" element={<EditorialEdit mode="edit" />} />
            <Route path="/admin/produccion/editorial/builder" element={<EditorialBuilder />} />
            <Route path="/admin/productos" element={<ProductsList />} />
            <Route path="/admin/productos/nuevo" element={<ProductCreate />} />
            <Route path="/admin/productos/:id" element={<ProductDetail />} />
            <Route path="/admin/miembros" element={<AdminMembers />} />
            <Route path="/admin/miembros/:id" element={<AdminMemberDetailWithPurchases />} />
            <Route path="/admin/suscripciones" element={<AdminSubscriptions />} />
            <Route path="/admin/crecimiento" element={<UnderConstruction title="Crecimiento" />} />
            <Route path="/admin/sistema" element={<UnderConstruction title="Sistema" />} />
          </Route>

          <Route path="/logout" element={<Logout />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
